<template>
    <div class="main orte-index">
        <div class="main-sidebar">
            <div class="main-sidebar__head">
                <h4 class="title">Einrichtungen</h4>
                <div class="btn-group" role="group" aria-label="Ansicht umschalten">
                    <button @click="switchToMapView" :class="['btn', showMap ? 'btn-active' : '']"
                        :aria-pressed="showMap" aria-label="Kartenansicht anzeigen" type="button">
                        Karte
                    </button>
                    <button @click="switchToListView" :class="['btn', !showMap ? 'btn-active' : '']"
                        :aria-pressed="!showMap" aria-label="Listenansicht anzeigen" type="button">
                        Liste
                    </button>
                </div>
            </div>

            <div v-if="tempShowHide" class="main-sidebar__filter">
                <div v-if="!expandFilter && !$isMobileScreen">
                    <keyword-search class="mb20" :focusMe="true" :initialValue="dataSet.query.solr"
                        @searchWordChanged="searchWordChanged" />

                    <anbieter-filter v-if="contentType !== null && sublocalityFilter !== null"
                        :contentType="contentType" :selectedFilters="selectedFilters"
                        :structuredFilters="structuredFilters" :sublocalityFilter="sublocalityFilter"
                        @filterChanged="setQueryFilters" @resetAllFilters="resetAllFilters" />

                    <div class="clearfix"></div>
                </div>

                <button @click="expandFilter = false" v-if="expandFilter" class="btn btn-expand btn-link"
                    ref="searchExpand" aria-label="Suche und Filter ausklappen">Suche und Filter ausklappen</button>
                <button @click="expandFilter = true" v-else class="btn btn-expand btn-link"
                    aria-label="Suche und Filter einklappen">Suche und Filter
                    einklappen</button>
            </div>

            <div tabindex="-1" class="main-sidebar__content"
                v-if="dataSet.data !== null && dataSet.data.length > 0 && showMap">
                <!-- data will be here -->
                <h6>{{ dataSet.total }} Ergebnisse</h6>
                <orte-card v-for="(item, index) in dataSet.data" :key="index" target="map" :item="item"
                    @highlight="highlightThisContent" :id="'vertical-content-' + item.id" />
            </div>

            <div v-else-if="!searching && showMap" class="search-no-results">
                <img src="/assets/search-no-result.png" alt="Keine Suchergebnisse">
                <h5 class="mt20">Keine Ergebnisse gefunden</h5>
            </div>

        </div>

        <div class="main-content" id="main-scroll-container">
            <TitleBar v-if="contentType !== null && $isMobileScreen" :karteLink="karteLink" :filters="allFilters"
                :selectedFilters="selectedFilters" :listeLink="listeLink" :showMapOrList="showMap" :isFixed="true"
                :showButtons="true" :contentCount="tempContentCount" :initialKeywordSearchValue="dataSet.query.solr"
                @contentFilteringFinished="filteringFinished" @resetAllFilters="resetAllFilters"
                @getNumberOfResults="getNumberOfResults" @searchWordChanged="searchWordChanged" />

            <div v-if="showMap" class="karte">

                <multiple-map ref="theMap" class="the-karte" :contents="dataSet.data"
                    @highlight="highlightThisContent" />

                <div class="karte-cards" v-if="dataSet.data !== null && dataSet.data.length > 0 && $isMobileScreen">
                    <div class="container-fluid">
                        <div class="row mobile--scrolling horizontal-scroll">
                            <map-card v-for="(item, index) in dataSet.data" :key="index" :item="item"
                                @highlight="highlightThisContent" :id="'horizontal-content-' + item.id" />
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="list-section section">
                <div class="container-fluid">
                    <div class="row cards-row" v-if="dataSet.data !== null && dataSet.data.length > 0">
                        <div class="col-12 col-sm-12 col-md-12 col-xl-4 cards" v-for="(item, index) in dataSet.data"
                            :key="index">
                            <orte-card :item="item" :isListView="true" />
                        </div>
                    </div>

                    <div v-else-if="!searching" class="search-no-results">
                        <img src="/assets/search-no-result.png" alt="Keine Suchergebnisse">
                        <h5 class="mt20">Keine Ergebnisse gefunden</h5>
                        <p class="mt20">Es wurden keine Einrichtungen gefunden, die Ihren Suchkriterien entsprechen.</p>
                        <button v-if="selectedFilters.size > 0 || dataSet.query.solr" @click="resetAllFilters"
                            class="btn btn-primary mt20">
                            Filter zurücksetzen
                        </button>
                    </div>

                </div>
            </div>

        </div>

        <main-category-select-modal @filterChanged="filterAndFocusChanged" :filterId="parseInt(353)"
            v-if="showMainCategorySelectModal && mainBasicFilter !== null" />

        <StructuredMoreFilters :showModal="filterModal" @on-close="updateOnClose" :contentCount="tempContentCount"
            :selectedFilters="selectedFilters" @getNumberOfResults="getNumberOfResults"
            :structuredFilters="structuredFilters" v-if="filterModal && structuredFilters !== null && !isMobileView"
            @filteringFinished="filteringFinished" />

    </div>
</template>

<script>
import Resource from '@/api/resource';
const contentTypeResource = new Resource('contenttypes');
import { getSublocalities } from '@/api/system';
import { getContentsOfType } from '@/api/content';
import { showLoader, hideLoader, getFilterFields, createSelectOptions, getAllowedValuesFromFieldType, getStructuredFilters, getBasicFilters, argsFilterMapToJSMap, isset } from '@/utils/helpers';
import { screenSizeMixin } from '@/mixins/screenSizeMixin';
import VueScrollTo from 'vue-scrollto';

export default {
    name: "Einrichtungen",
    mixins: [screenSizeMixin],
    components: {
        MapCard: () => import('./components/OrteMapCard.vue'),
        TitleBar: () => import('@/components/controls/TitleBar/TitleBar.vue'),
        OrteCard: () => import('@/components/cards/Ort.vue'),
        MultipleMap: () => import('@/components/map/multiple.vue'),
        MainCategorySelectModal: () => import('@/components/modals/MainCategorySelectModal.vue'),
        StructuredMoreFilters: () => import('@/components/modals/StructuredMoreFilters/StructuredMoreFilters.vue'),
        AnbieterFilter: () => import('@/components/filters/AnbieterFilter.vue'),
        KeywordSearch: () => import('@/components/controls/KeywordSearch'),
    },
    data() {
        return {
            tempShowHide: true,
            showMainCategorySelectModal: false,
            currentContentTypeId: this.$institutionId,
            tempContentCount: 0,
            searching: true,
            contentType: null,
            sublocalities: null,
            isMobileView: false,
            isInitializing: true, // Track if component is still initializing
            dataSet: {
                total: 0,
                data: [],
                meta: null,
                query: {
                    page: 1,
                    type: "teaser",
                    limit: 500,
                    solr: '',
                    filters: null,
                    selectedAppId: this.$appId,
                    selectedContentTypeId: this.currentContentTypeId,
                    view_status: 1,
                    sort: {
                        prop: '',
                        order: 'asc'
                    },
                },
            },
            selectedCategory: null,
            selectedFilters: new Map(),
            expandFilter: false,
            filterModal: false,
            scrollVerticalOptions: {
                container: '.main-sidebar__content',
                easing: 'ease-in',
                lazy: false,
                offset: -60,
                force: true,
                cancelable: true,
                x: false,
                y: true,
            },
            scrollHorizontalOptions: {
                container: '.horizontal-scroll',
                easing: 'ease-in',
                lazy: false,
                offset: -60,
                force: true,
                cancelable: true,
                x: true,
                y: false,
            },
        }
    },
    created() {
        this.getInstitutionSettings();
        var filters = argsFilterMapToJSMap(this.uriFilters);
        if (isset(filters)) {
            this.filteringFinished(filters);
            this.showMainCategorySelectModal = false;
        }
        else {
            this.showMainCategorySelectModal = true;
        }
        // Filter standardmäßig ausgeklappt nur auf Desktop und in Listenansicht
        if (!this.$isMobileScreen && !this.showMap) {
            this.expandFilter = false; // false = ausgeklappt
        } else {
            this.expandFilter = true; // true = eingeklappt
        }
        // Mark initialization as complete after a short delay to allow watchers to settle
        this.$nextTick(() => {
            this.isInitializing = false;
        });
    },
    watch: {
        $screenWidth(newWidth) {
            if (newWidth <= 991) {
                this.isMobileView = true;
            }
            else {
                this.isMobileView = false;
            }
        },
        '$route.query'(newQuery, oldQuery) {
            // Skip syncing during initialization - created() hook handles initial URL state
            if (this.isInitializing) {
                return;
            }
            // Sync filters and search word from URL to component state when route changes
            // This ensures filters are preserved when switching between map and list views
            var oldFilters = oldQuery && oldQuery.filters !== undefined ? oldQuery.filters : null;
            if (newQuery.filters !== oldFilters) {
                var filters = argsFilterMapToJSMap(newQuery.filters);
                if (isset(filters)) {
                    // Only update if different to avoid unnecessary updates
                    var currentFiltersStr = this.dataSet.query.filters ? JSON.stringify(Array.from(this.selectedFilters.entries())) : null;
                    var newFiltersStr = JSON.stringify(Array.from(filters.entries()));
                    if (currentFiltersStr !== newFiltersStr) {
                        this.selectedFilters = filters;
                        this.dataSet.query.filters = newFiltersStr;
                    }
                } else if (newQuery.filters === undefined || newQuery.filters === null) {
                    // Only clear filters if explicitly removed from URL and we have filters set
                    if (this.selectedFilters.size > 0) {
                        this.selectedFilters = new Map();
                        this.dataSet.query.filters = null;
                    }
                }
            }
            // Sync search word from URL
            var oldSolr = oldQuery && oldQuery.solr !== undefined ? oldQuery.solr : null;
            if (newQuery.solr !== oldSolr) {
                if (newQuery.solr !== undefined && newQuery.solr !== null && newQuery.solr !== '') {
                    if (this.dataSet.query.solr !== newQuery.solr) {
                        this.dataSet.query.solr = newQuery.solr;
                    }
                } else {
                    if (this.dataSet.query.solr !== '') {
                        this.dataSet.query.solr = '';
                    }
                }
            }
        }
    },
    computed: {
        uriFilters() {
            return this.$route.query.filters;
        },
        showMap() {
            if (Object.prototype.hasOwnProperty.call(this.$route.query, 'show') && this.$route.query.show === 'karte') {
                return true;
            }
            else if (Object.prototype.hasOwnProperty.call(this.$route.query, 'show') && this.$route.query.show === 'liste') {
                return false;
            }
            // Default: show map on desktop, list on mobile
            return this.$isDesktopScreen;
        },
        structuredFilters() {
            return getStructuredFilters(this.contentType);
        },
        basicFilters() {
            var filters = getBasicFilters(this.structuredFilters);

            if (this.sublocalityFilter !== null) {
                filters.push(this.sublocalityFilter);
            }
            return filters;
        },
        allFilters() {
            return this.structuredFilters.concat(this.sublocalityFilter);
        },
        sublocalityFilter() {
            if (this.sublocalities !== null) {
                var bla = {};
                bla.field_type_id = "location";
                bla.label = "Stadtteile";
                bla.options = []
                for (var i = 0; i < this.sublocalities.length; i++) {
                    bla.options.push({ label: this.sublocalities[i].sublocality, value: this.sublocalities[i].sublocality });
                }
                return bla;
            }
            return null;
        },
        mainBasicFilter() {
            if (this.basicFilters !== null && this.basicFilters.length > 0) {
                var result = this.basicFilters.splice(0, 1);
                return result[0];
            }
            return null;
        },
        karteLink() {
            // Build link with all query parameters preserved
            const query = { ...this.$route.query, show: 'karte' };
            // Ensure filters and solr are included from component state if not in route query
            if (this.dataSet.query.filters && !query.filters) {
                query.filters = this.dataSet.query.filters;
            }
            if (this.dataSet.query.solr && !query.solr) {
                query.solr = this.dataSet.query.solr;
            }
            return { path: '/orte', query };
        },
        listeLink() {
            // Build link with all query parameters preserved
            const query = { ...this.$route.query, show: 'liste' };
            // Ensure filters and solr are included from component state if not in route query
            if (this.dataSet.query.filters && !query.filters) {
                query.filters = this.dataSet.query.filters;
            }
            if (this.dataSet.query.solr && !query.solr) {
                query.solr = this.dataSet.query.solr;
            }
            return { path: '/orte', query };
        },
    },
    methods: {
        showLoader,
        hideLoader,
        getSublocalities,
        createSelectOptions,
        getAllowedValuesFromFieldType,
        switchToMapView() {
            // Filter eingeklappt in Kartenansicht
            this.expandFilter = true;
            // Preserve all existing query parameters (filters, solr, etc.) when switching views
            const query = { 
                ...this.$route.query, 
                show: 'karte'
            };
            // Ensure filters and solr are included from component state if not in route query
            if (this.dataSet.query.filters && !query.filters) {
                query.filters = this.dataSet.query.filters;
            }
            if (this.dataSet.query.solr && !query.solr) {
                query.solr = this.dataSet.query.solr;
            }
            this.$router.push({ path: '/orte', query });
        },
        switchToListView() {
            // Filter ausgeklappt nur auf Desktop in Listenansicht
            if (!this.$isMobileScreen) {
                this.expandFilter = false; // false = ausgeklappt
            } else {
                this.expandFilter = true; // true = eingeklappt auf Mobile
            }
            // Preserve all existing query parameters (filters, solr, etc.) when switching views
            const query = { 
                ...this.$route.query, 
                show: 'liste'
            };
            // Ensure filters and solr are included from component state if not in route query
            if (this.dataSet.query.filters && !query.filters) {
                query.filters = this.dataSet.query.filters;
            }
            if (this.dataSet.query.solr && !query.solr) {
                query.solr = this.dataSet.query.solr;
            }
            this.$router.push({ path: '/orte', query });
        },
        filterAndFocusChanged(fieldTypeId, values) {
            this.filterChanged(fieldTypeId, values);
            if (this.$refs.searchExpand) {
                this.$refs.searchExpand.focus();
            }
        },
        filterChanged(fieldTypeId, values) {
            this.selectedFilters.set(fieldTypeId, values);
            this.dataSet.query.filters = JSON.stringify(Array.from(this.selectedFilters.entries()));
            this.getContents();
        },
        setQueryFilters(selectedFilters) {
            this.dataSet.query.page = 1;
            this.selectedFilters = selectedFilters;
            this.dataSet.query.filters = JSON.stringify(Array.from(this.selectedFilters.entries()));
            this.getContents();
        },
        getInstitutionSettings(val) {
            this.dataSet.query.selectedContentTypeId = this.currentContentTypeId;
            this.$scrollTo();
            this.getContentType();
            this.getTheSublocalities();
        },
        searchWordChanged(word) {
            this.dataSet.query.solr = word;
            // Update URL immediately to keep it in sync
            const query = { ...this.$route.query };
            if (word) {
                query.solr = word;
            } else {
                delete query.solr;
            }
            // Preserve filters and show parameter
            if (this.dataSet.query.filters) {
                query.filters = this.dataSet.query.filters;
            }
            if (!query.show) {
                query.show = this.$isDesktopScreen ? 'karte' : 'liste';
            }
            this.$router.replace({ path: '/orte', query }).catch(() => {
                // Ignore navigation duplicated errors
            });
            this.getContents();
        },
        getNumberOfResults(map) {
            /*tempFilters will contain all the filters set for now but only for the purpose of calculating the number of items found for these filtersettings*/
            var tempFilters = new Map([...this.selectedFilters, ...map]);
            /*#todo: we'll have to start a call to the backend to find out how many values there are for this selection*/
            this.getNumberOfContentsMatchingTheFilter(tempFilters);
        },
        filteringFinished(map) {
            /*get's invoked when the user closes the more-filters-modal with the show results-btn this also means, that we'll have to start searching now*/
            this.selectedFilters = map;
            this.filterModal = false;
            this.dataSet.query.filters = JSON.stringify(Array.from(this.selectedFilters.entries()));
            this.getContents();
        },
        resetAllFilters(selectedFilters) {
            this.tempShowHide = false;
            // Clear all filters and reset the query
            this.selectedFilters = new Map();
            this.dataSet.query.filters = null;
            this.dataSet.query.page = 1;
            this.getContents();
            this.$nextTick(() => {
                this.tempShowHide = true;
            });
        },
        updateOnClose() {
            this.filterModal = false;
        },
        getContentType() {
            this.contentType = null;
            var id = this.currentContentTypeId;
            this.loader = this.showLoader(this.loader);
            /*overwrite the existing filters*/
            this.selectedFilters = new Map();
            contentTypeResource.get(id).then(response => {
                this.contentType = response.data;
            })
                .finally(() => {
                    this.loader = this.hideLoader(this.loader);
                });
        },
        async getTheSublocalities() {
            var id = this.currentContentTypeId;
            getSublocalities(this.$city, null, id).then(response => {
                this.sublocalities = response;
            });
        },
        getContents() {
            this.searching = true;
            // Use router.replace instead of history.pushState to keep Vue Router in sync
            // This ensures filters and search word are preserved when switching views
            const query = { ...this.$route.query };
            if (this.dataSet.query.filters) {
                query.filters = this.dataSet.query.filters;
            } else {
                // Remove filters from query if null/empty
                delete query.filters;
            }
            if (this.dataSet.query.solr) {
                query.solr = this.dataSet.query.solr;
            } else {
                // Remove solr from query if empty
                delete query.solr;
            }
            // Preserve the show parameter (karte/liste)
            if (!query.show) {
                // Set default based on screen size if not explicitly set
                query.show = this.$isDesktopScreen ? 'karte' : 'liste';
            }
            this.$router.replace({ path: '/orte', query }).catch(() => {
                // Ignore navigation duplicated errors
            });
            this.dataSet.query.type = "view";
            var contentLoader = this.showLoader(contentLoader);
            const { limit, page } = this.dataSet.query;
            getContentsOfType(this.currentContentTypeId, this.dataSet.query).then(response => {
                const { data, meta } = response;
                this.dataSet.data = data;
                this.dataSet.data.forEach((element, index) => {
                    element['index'] = (page - 1) * limit + index + 1;
                });
                this.dataSet.meta = meta;
                this.dataSet.total = meta.total;
                this.tempContentCount = meta.total;
            })
                .finally(() => {
                    contentLoader = this.hideLoader(contentLoader);
                    document.getElementById('main-scroll-container').scroll({ top: 0, behavior: 'smooth' });
                    this.tempShowHide = true;
                    this.searching = false;

                });
        },
        getNumberOfContentsMatchingTheFilter(tempFilters) {
            this.dataSet.query.type = "count";
            this.dataSet.query.filters = JSON.stringify(Array.from(tempFilters.entries()));
            var contentLoader = this.showLoader(contentLoader);
            getContentsOfType(this.currentContentTypeId, this.dataSet.query).then(response => {
                this.tempContentCount = response;
            })
                .finally(() => {
                    contentLoader = this.hideLoader(contentLoader);
                });
        },
        highlightThisContent(params) {
            Object.keys(this.dataSet.data).forEach((k) => {
                this.dataSet.data[k].highlight = false;
                if (this.dataSet.data[k].id == params.content.id) {
                    this.dataSet.data[k].highlight = true;

                    if (params.sender != "map") {
                        /*open the popup*/
                        this.$refs.theMap.openPopup(params.content);
                    }
                    else {
                        if (this.$isMobileScreen) {
                            VueScrollTo.scrollTo(('#horizontal-content-' + this.dataSet.data[k].id), '2000', this.scrollHorizontalOptions);
                        }
                        else {
                            VueScrollTo.scrollTo(('#vertical-content-' + this.dataSet.data[k].id), '2000', this.scrollVerticalOptions);
                        }
                    }
                }
            });
        }
    }
};
</script>
<style lang="scss" scoped>
@import '@/scss/_variables.scss';

// Modern CSS Grid layout for sidebar and content
// This replaces the fixed positioning approach with a more maintainable grid layout
.main.orte-index {
    // Constrain to viewport width to prevent overflow
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    box-sizing: border-box;

    // Use CSS Grid for better layout control on desktop
    @media (min-width: 992px) {
        display: grid;
        grid-template-columns: 384px 1fr;
        grid-template-areas: "sidebar content";
        gap: 0 10px; // row-gap column-gap (10px spacing between sidebar and content)
        align-items: start;
    }

    .main-sidebar {
        @media (min-width: 992px) {
            // Override fixed positioning - use grid positioning instead
            position: relative;
            top: auto;
            left: auto;
            grid-area: sidebar;
            width: 100%;
            max-width: 384px;
            height: calc(100vh - #{$desktop-header-size} - #{$footer-height});
            // Remove margin - use grid gap instead
            margin-right: 0;
            // Maintain z-index for box-shadow layering
            z-index: 999;
            box-sizing: border-box;
        }
    }

    .main-content {
        @media (min-width: 992px) {
            // Remove manual margin - grid handles positioning automatically
            margin-left: 0;
            grid-area: content;
            height: calc(100vh - #{$desktop-header-size} - #{$footer-height});
            // Prevent grid item from overflowing - critical for grid layouts
            min-width: 0;
            width: 100%;
            max-width: 100%;
            overflow-x: hidden;
            // Ensure box-sizing is correct
            box-sizing: border-box;

            // Ensure container-fluid respects width constraints
            .container-fluid {
                max-width: 100%;
                width: 100%;
                box-sizing: border-box;
                // Padding is fine as long as box-sizing is border-box
            }

            // Ensure map view doesn't overflow
            .karte {
                width: 100%;
                max-width: 100%;
                overflow-x: hidden;
                box-sizing: border-box;
            }

            // Ensure row class doesn't cause overflow (Bootstrap rows have negative margins)
            .row {
                margin-left: 0;
                margin-right: 0;
                width: 100%;
                max-width: 100%;
                box-sizing: border-box;
            }
        }
    }
}

.search-no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    width: 100%;
    text-align: center;
    padding: 40px 20px;

    img {
        width: 110px;
        margin-bottom: 20px;
    }

    h5 {
        font-weight: 700;
        margin-bottom: 10px;
    }

    p {
        color: $text-light;
        max-width: 500px;
        margin: 0 auto;
    }

    .btn {
        margin-top: 20px;
    }
}

.karte {
    overflow: hidden;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    .the-karte {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;

        @media (max-width: 991px) {
            margin-top: 64px;
        }
    }

    .karte-cards {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
    }
}

.main-sidebar__content {
    overflow-y: auto;
}

.main-sidebar_scroll {
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    height: calc(100vh - 320px);
    scrollbar-width: none;
}

.main-sidebar__head {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .btn-group {
        margin-left: auto;
    }
}

.list-section {
    margin-top: 15px !important;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;

    @media (max-width: 991px) {
        padding: 0px;
        padding-top: 50px;
    }

    @media (min-width: 992px) {
        // Ensure list section doesn't overflow in grid layout
        min-width: 0;
    }
}

.btn-group {
    box-shadow: 2px 4px 4px rgba(0, 0, 0, 0.1);
    background-color: $white-color;
    border-radius: 100px;
    padding: 5px;
    margin-left: 15px;

    .btn {
        border: none;
        padding: 0 0.75rem;
        height: 30px;
        background-color: transparent;
        color: $dark-color;
        font-weight: 400;
        line-height: 30px;

        &-active {
            background-color: $primary !important;
            color: #FFF;
            border-radius: 100px !important;
            font-weight: 700;
        }

        &:focus-visible {
            outline: 2px solid $primary;
            outline-offset: 2px;
        }
    }
}

.cards-row {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
    box-sizing: border-box;

    // Override any negative margins from Bootstrap row class
    @media (min-width: 992px) {
        margin-left: 0;
        margin-right: 0;
    }
}

.cards {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    // Ensure cards don't overflow
    min-width: 0;
    box-sizing: border-box;
    // Make all cards stretch to same height - align-self ensures it stretches even with Bootstrap columns
    align-self: stretch;

    @media (max-width: 1600px) {
        flex: 0 0 50%;
        max-width: 50%;
        align-self: stretch; // Ensure stretching even with fixed flex-basis
    }

    @media (min-width: 992px) and (max-width: 1250px) {
        flex: 0 0 100%;
        max-width: 100%;
    }

    @media (max-width: 750px) {
        flex: 0 0 100%;
        max-width: 100%;
    }

    .card-link {
        height: 100%;
        display: flex;
        flex-direction: column;
        flex: 1;
    }

    .card-link .card {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 100%;
    }

}

body[dir="rtl"] {
    .main-sidebar__head {
        .btn-group {
            margin-left: 0;
            margin-right: auto;
        }
    }
}
</style>
